// 권리사 「할 일」 공유 상태 — 상단바 「검수/화리」 배지 · 작품 탭 「오늘 할 일」 카드 · 검수/화리 탭 「할 일 전체」가 같은 숫자를 본다(2026-10-21).
//   읽기: src/lib/rightsTodoApi.js · 판정: src/lib/rightsTodo.js
//   다시 읽는 때: 처음 · 화면(주소)이 바뀔 때(30초에 한 번까지) · 창으로 돌아올 때(30초에 한 번까지) · 5분마다(창이 보일 때만) · 처리 직후 refresh().
//   실패하면 이전 숫자를 그대로 두고 error 만 채운다(0 으로 덮으면 「할 일 없음」으로 잘못 읽힌다).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "./UserContext";
import { fetchRightsTodoSources } from "../lib/rightsTodoApi";
import { buildRightsTodo, EMPTY_RIGHTS_TODO } from "../lib/rightsTodo";

const MIN_GAP_MS = 30 * 1000;
const POLL_MS = 5 * 60 * 1000;

const RightsTodoContext = createContext({
  todo: EMPTY_RIGHTS_TODO,
  works: [],
  loaded: false,
  loading: false,
  error: null,
  refresh: async () => {},
});

export function RightsTodoProvider({ children }) {
  const { userInfo } = useUser();
  const location = useLocation();
  const company = userInfo?.company || "";
  const enabled = Boolean(company) && Boolean(userInfo?.role?.includes("Copyrights Holder"));

  const [state, setState] = useState({ todo: EMPTY_RIGHTS_TODO, works: [], loaded: false, loading: false, error: null });
  const seqRef = useRef(0);
  const lastRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const seq = ++seqRef.current;
    lastRef.current = Date.now();
    setState((s) => ({ ...s, loading: true }));
    try {
      const sources = await fetchRightsTodoSources(company);
      if (seq !== seqRef.current) return;
      setState({ todo: buildRightsTodo({ ...sources, now: Date.now() }), works: sources.works, loaded: true, loading: false, error: null, loadedAt: Date.now() });
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error("할 일 조회 실패:", err);
      setState((s) => ({ ...s, loading: false, error: err?.message || String(err) }));
    }
  }, [enabled, company]);

  const refreshIfStale = useCallback(() => {
    if (Date.now() - lastRef.current >= MIN_GAP_MS) refresh();
  }, [refresh]);

  // 회사가 정해지면(또는 바뀌면) 바로 — 다른 회사의 숫자가 남지 않게 비우고 읽는다
  useEffect(() => {
    seqRef.current += 1;
    lastRef.current = 0;
    setState({ todo: EMPTY_RIGHTS_TODO, works: [], loaded: false, loading: false, error: null });
    if (enabled) refresh();
  }, [enabled, company, refresh]);

  useEffect(() => {
    refreshIfStale();
  }, [location.pathname, refreshIfStale]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => refreshIfStale();
    const onVisible = () => { if (document.visibilityState === "visible") refreshIfStale(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, POLL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [enabled, refresh, refreshIfStale]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <RightsTodoContext.Provider value={value}>{children}</RightsTodoContext.Provider>;
}

export function useRightsTodo() {
  return useContext(RightsTodoContext);
}
